import {ChildCaseModel} from '../model/child-case.model';
import {VisitModel} from '../model/visit.model';
import {TreatmentActionModel} from '../model/treatment-action.model';
import moment from 'moment/moment';
import {SymptomsUtil} from '../util/symptoms.util';
import {WomanCaseModel} from '../model/woman-case.model';
import {WomanCaseTypeEnum} from '../constants/woman-case-type.enum';
import {MalnutritionLevel} from '../constants/malnutrition-level.enum';
import {HospitalizationCause} from '../constants/hospitalization-cause';
import {DateUtil} from '../util/date.util';

export class HospitalHelper {


    public readonly TAKE_CHILD_TO_HOSPITAL: string = 'Llevaré al niño al hospital';
    public readonly TAKE_WOMAN_TO_HOSPITAL: string = 'Lleve a esta mujer al hospital de inmediato.';
    public readonly NOTICE_FAMILY_TO_TAKE_CHILD_TO_HOSPITAL: string = 'He informado a la familia para llevar al niño al hospital';
    public readonly NOTICE_FAMILY_TO_TAKE_WOMAN_TO_HOSPITAL: string = 'He informado a la familia para llevar a la mujer al hospital';

    /**
     *  Verifies the need for the child to be taken to hospital.
     *  More Information in the README.me file at Business Rule XX // TODO add role indicator
     *
     * @param childCase Current case
     */
    private getHospitalizationConditions(childCase: ChildCaseModel) {
        const containsGeneralizedEdemas = SymptomsUtil.containsGeneralizedEdema(childCase);
        const containsEdemasInBothFeet = SymptomsUtil.containsEdemaInBothFeet(childCase);
        const haveNoAppetite = SymptomsUtil.haveNoAppetite(childCase);
        const haveComplications = SymptomsUtil.haveComplications(childCase);
        const sd3Neg = MalnutritionLevel.SEVERE_ACUTE_MALNUTRITION === childCase.marker;
        const sd2Neg = MalnutritionLevel.MODERATE_ACUTE_MALNUTRITION === childCase.marker;
        // Edema generalizado só conta (hospital + severo) até 10 anos — ver ChildMuacService.
        const months = DateUtil.monthsDiff(childCase.childBirthDate, childCase.date);
        const edemaCounts = containsGeneralizedEdemas && months != null && !isNaN(months) && months <= 120;

        return [
            // MUAC SD3- ou SD2- + edema em ambos os pés -> hospital.
            {condition: (sd3Neg || sd2Neg) && containsEdemasInBothFeet, cause: HospitalizationCause.CONDITION_1},
            // Edema generalizado (< 10 anos) -> hospital + desnutrición aguda severa,
            // INDEPENDENTE do valor do MUAC (o marker já é forçado a severo em visit-results).
            {condition: edemaCounts, cause: HospitalizationCause.CONDITION_2},
            {condition: haveNoAppetite && haveComplications, cause: HospitalizationCause.CONDITION_3},
            {condition: haveNoAppetite && childCase.anotherDisease != null, cause: HospitalizationCause.CONDITION_4}
        ];
    }

    isNecessaryForwardToHospital(childCase: ChildCaseModel): boolean {
        return this.getHospitalizationConditions(childCase).some(c => c.condition);
    }

    getHospitalizationCauses(childCase: ChildCaseModel) {
        return this.getHospitalizationConditions(childCase)
            .filter(c => c.condition)
            .map(c => c.cause);
    }

    isNecessaryForwardWomanToHospital(womanCase: WomanCaseModel): boolean {
        const {
            type,
            complicationsSymptoms,
            obstetricDanger,
            puerperiumDanger,
            postpartumDanger
        } = womanCase;

        const isPregnant = type === WomanCaseTypeEnum.PREGNANT;
        const isLactating = type === WomanCaseTypeEnum.LACTATING;

        if (isPregnant) {
            return !!(complicationsSymptoms?.length || obstetricDanger?.length);
        }

        if (isLactating) {
            return !!(puerperiumDanger?.length || postpartumDanger?.length);
        }

        return false;
    }

    isNecessaryForwardToHospitalAtVisit(visit: VisitModel): boolean {
        const muacIndex = visit.muac;
        const containsGeneralizedEdemas = SymptomsUtil.visitContainsGeneralizedEdema(visit);
        const containsEdemasInBothFeet = SymptomsUtil.visitContainsEdemaInBothFeet(visit);
        const haveNoAppetite = SymptomsUtil.haveNoAppetiteAtVisit(visit);
        const haveComplications = SymptomsUtil.haveComplicationsAtVisit(visit);

        // @ts-ignore
        const condition1 = (muacIndex <= 11.5 && containsEdemasInBothFeet);
        // @ts-ignore
        const condition2 = (muacIndex <= 12.5 && containsGeneralizedEdemas);
        const condition3 = (haveNoAppetite && visit.anotherDisease != null);
        const condition4 = (haveNoAppetite && haveComplications);

        return condition1 || condition2 || condition3 || condition4;
    }

    prepareVisitToHospital(visit: VisitModel, hospitalizationCauses?: string[]) {
        visit.forwardToHospital = true;
        visit.instructions = ['Lleve a este niño al hospital de inmediato.'];
        visit.sachetImage = null;
        visit.sachetsKcal = null;
        visit.dailySachetsCount = null;
        visit.alert = 'Referir al hospital';
        visit.takeChildToHospital = new TreatmentActionModel(this.TAKE_CHILD_TO_HOSPITAL, true);
        visit.nextVisit = moment(visit.date).add(7, 'days').format('DD/MM/YYYY');

        if (hospitalizationCauses || hospitalizationCauses.length > 0) {
            visit.hospitalizationCauses = hospitalizationCauses;
        }
    }


    prepareWomanVisitToHospital(visit: VisitModel) {
        visit.forwardToHospital = true;
        visit.instructions = [this.TAKE_WOMAN_TO_HOSPITAL];
        visit.sachetImage = null;
        visit.sachetsKcal = null;
        visit.dailySachetsCount = null;
        visit.alert = 'Referir al hospital';
        visit.takeChildToHospital = new TreatmentActionModel(this.TAKE_WOMAN_TO_HOSPITAL, true);
        visit.nextVisit = moment(visit.date).add(7, 'days').format('DD/MM/YYYY');
        return visit;
    }

    prepareChildCaseToHospital(childCase: ChildCaseModel) {
        // For older versions
        childCase.iconAlert = {
            icon: 'hospital-symbol',
            class: 'hospital-icon'
        };
        // For new versions
        childCase.attentionIcon = 'faHospitalSymbol';
        childCase.hospitalCase = true;
    }

    prepareWomanCaseToHospital(womanCase: WomanCaseModel) {
        // For older versions
        womanCase.iconAlert = {
            icon: 'hospital-symbol',
            class: 'hospital-icon'
        };
        // For new versions
        womanCase.attentionIcon = 'faHospitalSymbol';
        womanCase.womanReferredToTheHospital = true;

        return womanCase;
    }

    // private isNecessaryForwardWomanToHospital(womanCase: WomanCaseModel) {
    //     const hasComplications = womanCase.complicationsSymptoms != null && womanCase.complicationsSymptoms.length > 0;
    //     const hasObstetricDanger = womanCase.obstetricDanger != null && womanCase.obstetricDanger.length > 0;
    //     const hasPostpartumDanger = womanCase.postpartumDanger != null && womanCase.postpartumDanger.length > 0;
    //     const puerperiumDanger = womanCase.puerperiumDanger != null && womanCase.puerperiumDanger.length > 0;
    //
    //     return hasComplications || hasObstetricDanger ||
    //         hasPostpartumDanger || puerperiumDanger;
    // }

}
